import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calendarService } from './calendarService';

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  googleToken: string | null;
  googleRefreshToken: string | null;
  googleTokenExpiry: number | null;
  login: (username: string, password: string) => Promise<void>;
  authenticateGoogle: () => Promise<void>;
  refreshGoogleToken: () => Promise<string>;
  setGoogleTokens: (accessToken: string, refreshToken?: string | null, expiryTime?: number | null) => void;
  logout: () => void;
  initialize: () => Promise<void>;
  refreshOrAuthenticate: () => Promise<void>;
}

const API_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL || 'https://backend.iceking.wtf';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      token: null,
      googleToken: null,
      googleRefreshToken: null,
      googleTokenExpiry: null,
      
      login: async (username: string, password: string) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
          });

          if (!response.ok) {
            throw new Error('Login failed');
          }

          const data = await response.json();
          set({ token: data.token });
          await get().authenticateGoogle();
        } catch (error) {
          console.error('Login error:', error);
          set({ isAuthenticated: false });
          throw error;
        }
      },
      
      authenticateGoogle: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/google/auth-url`);
          const data = await response.json();

          if (data.authUrl) {
            window.location.href = data.authUrl;
            return new Promise<void>((resolve) => {
              set({ isAuthenticated: false });
              // This will only run if the redirect fails
              resolve();
            });
          } else {
            set({ isAuthenticated: false });
            throw new Error('Failed to get authentication URL');
          }
        } catch (error) {
          set({ isAuthenticated: false });
          console.error('Google authentication error:', error);
          throw error;
        }
      },
      
      refreshGoogleToken: async () => {
        const refreshToken = get().googleRefreshToken;

        if (!refreshToken) {
          set({ isAuthenticated: false });
          throw new Error('No refresh token available');
        }

        try {
          const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (!response.ok) {
            const errorData = await response.text();
            console.error('Token refresh error response:', errorData);
            set({ isAuthenticated: false });
            throw new Error(`Failed to refresh token: ${response.status}`);
          }

          const { accessToken, expiryDate } = await response.json();
          // Update tokens in store
          set({
            googleToken: accessToken,
            googleTokenExpiry: expiryDate || (Date.now() + 3600000),
            isAuthenticated: true
          });

          return accessToken;
        } catch (error) {
          console.error('Token refresh error:', error);
          set({ googleToken: null, googleRefreshToken: null, googleTokenExpiry: null });
          throw error;
        }
      },
      
      setGoogleTokens: async (accessToken, refreshToken = null, expiryTime = null) => {
        set({
          googleToken: accessToken,
          googleRefreshToken: refreshToken !== null ? refreshToken : get().googleRefreshToken,
          googleTokenExpiry: expiryTime !== null ? expiryTime : get().googleTokenExpiry,
          isAuthenticated: true
        });
      },
      
      logout: () => {
        set({ 
          isAuthenticated: false, 
          token: null, 
          googleToken: null,
          googleRefreshToken: null,
          googleTokenExpiry: null 
        });
      },
      
      initialize: async () => {
        const { googleToken, googleRefreshToken, googleTokenExpiry } = get();
        // Case 1: Valid token exists - use it
        if (googleToken && googleTokenExpiry && Date.now() < googleTokenExpiry - 300000) { // 5-minute buffer
          console.log("initialize case1")
          try {
            await calendarService.setAccessToken(googleToken);
          } catch (error) {
            set({ isAuthenticated: false });
            console.error('Failed to restore Google token:', error);
            await get().refreshOrAuthenticate();
          }
        } 
        // Case 2: Token expired but refresh token exists - refresh it
        else if (googleToken && googleRefreshToken) {
          console.log("initialize case2")
          try {
            await calendarService.setAccessToken(googleToken);
            await get().refreshGoogleToken();
          } catch (error) {
            console.error('Failed to refresh token:', error);
            set({isAuthenticated: false});
          }
        }
        else {
          console.log("isAuthenticated false");
          set({isAuthenticated: false});
        }
        // Case 3: No tokens - user will need to authenticate
      },
      
      refreshOrAuthenticate: async () => {
        // First try to refresh if we have a refresh token
        const { googleRefreshToken } = get();
        
        if (googleRefreshToken) {
          try {
            await get().refreshGoogleToken();
            return;
          } catch (error) {
            console.error('Refresh failed, need to re-authenticate:', error);
            try {
              await get().authenticateGoogle();
            } catch (error) {
              console.error('Re-authentication failed:', error);
              throw error;
            }
          }
        }
      }
    }),
    {
      name: 'auth-storage',
    }
  )
);

// Initialize auth on page load
useAuthStore.getState().initialize();